import { faClock } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Gift } from '#src/atoms/icons/Gift.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { Horizontal } from '#src/atoms/layout/flex/index.tsx';
import { LabelS } from '#src/atoms/typography/index.tsx';
import { TimeLeft } from '#src/contexts/ticker/TimeLeft/index.tsx';
import { useTimeLeft } from '#src/hooks/useTimeLeft.tsx';

const Dot = styled.div`
  background-color: var(--c-white);
  border-radius: 50%;
  width: var(--half-unit);
  height: var(--half-unit);
`;

const Tag = styled(Horizontal).attrs({ gap: 0.5, center: true })`
  border-radius: 3px 3px 14px 14px;
  padding: 0 var(--half-unit);
  width: 100%;
  white-space: nowrap;

  &.compact {
    border-radius: var(--double-unit);
  }
`;

const LiveTag = styled(Tag)`
  background-color: var(--c-red-400);
`;

const RewardsTag = styled(Tag)`
  background-color: var(--c-onBase-success);
  color: var(--c-black);
`;

const LiveLabel = styled(LabelS)`
  text-transform: uppercase;
`;

const NextUpcomingTagWrapper = styled(Tag)`
  background-color: var(--c-yellow-400);
  & > svg {
    font-size: 0.675rem;
  }
`;

type NextUpcomingTagProps = {
  startDate: Date;
  compact?: boolean;
};
const NextUpcomingTag = ({ startDate, compact }: NextUpcomingTagProps) => {
  const { message } = useTimeLeft(startDate);
  return (
    <NextUpcomingTagWrapper className={classNames({ compact })}>
      {!compact && (
        <FontAwesomeIcon icon={faClock} size="xs" color="var(--c-black)" />
      )}
      <LabelS as="p" bold color="var(--c-black)">
        {message}
      </LabelS>
    </NextUpcomingTagWrapper>
  );
};

type Props = {
  isLive: boolean;
  isUpcoming: Nullable<boolean>;
  startDate: Nullable<Date>;
  expectedRewardsDistributionDate: Nullable<Date>;
  waitingForRewards: Nullable<boolean>;
  compact?: boolean;
};

export const FixtureItemTag = ({
  isLive,
  isUpcoming,
  startDate,
  expectedRewardsDistributionDate,
  waitingForRewards,
  compact,
}: Props) => {
  if (isLive) {
    return (
      <LiveTag className={classNames({ compact })}>
        <Dot />
        <LiveLabel as="p" bold color="var(--c-white)">
          <FormattedMessage id="FixturePickerItem.live" defaultMessage="Live" />
        </LiveLabel>
      </LiveTag>
    );
  }
  if (isUpcoming && startDate) {
    return <NextUpcomingTag compact={compact} startDate={startDate} />;
  }

  if (waitingForRewards && expectedRewardsDistributionDate) {
    return (
      <RewardsTag className={classNames({ compact })}>
        <Gift size={1.5} />
        <LabelS as="p" bold>
          <TimeLeft time={expectedRewardsDistributionDate} />
        </LabelS>
      </RewardsTag>
    );
  }
  return null;
};
